import { useEffect, useState } from 'react';

export type HolidayProductSelectorExperimentSlot =
	| 'control'
	| 'variant-1'
	| 'variant-2';

export const TARGET_POLL_INTERVAL_MS = 250;
export const TARGET_TIMEOUT_MS = 10_000;

const holidayProductSelectorSlots: string[] = [
	'control',
	'variant-1',
	'variant-2'
];

const isHolidayProductSelectorSlot = (
	value: string | undefined
): value is HolidayProductSelectorExperimentSlot =>
	Boolean(value && holidayProductSelectorSlots.includes(value));

type ExperimentListener = (
	variant: HolidayProductSelectorExperimentSlot
) => void;

const experimentListeners = new Set<ExperimentListener>();
let resolvedVariant: HolidayProductSelectorExperimentSlot = 'control';
let pollingStartedAt = 0;
let pollingInterval: number | undefined;
let pollingComplete = false;

declare global {
	interface Window {
		vaTargetExperiments?: {
			holidayProductSelector?: string;
		};
	}
}

const getTargetHolidayProductSelectorExperimentSlot = ():
	| string
	| undefined => {
	if (typeof window === 'undefined') return undefined;

	return window.vaTargetExperiments?.holidayProductSelector;
};

const stopPolling = () => {
	if (pollingInterval !== undefined) {
		window.clearInterval(pollingInterval);
		pollingInterval = undefined;
	}
};

const applyTargetDecision = () => {
	const decision = getTargetHolidayProductSelectorExperimentSlot();

	if (decision === undefined) return false;

	pollingComplete = true;
	stopPolling();

	if (isHolidayProductSelectorSlot(decision)) {
		resolvedVariant = decision;
		experimentListeners.forEach((listener) => listener(decision));
	}

	return true;
};

const subscribeToTargetDecision = (listener: ExperimentListener) => {
	experimentListeners.add(listener);
	listener(resolvedVariant);

	if (!pollingComplete && pollingInterval === undefined) {
		pollingStartedAt = Date.now();

		if (!applyTargetDecision()) {
			pollingInterval = window.setInterval(() => {
				if (
					applyTargetDecision() ||
					Date.now() - pollingStartedAt >= TARGET_TIMEOUT_MS
				) {
					pollingComplete = true;
					stopPolling();
				}
			}, TARGET_POLL_INTERVAL_MS);
		}
	}

	return () => {
		experimentListeners.delete(listener);

		if (experimentListeners.size === 0) {
			stopPolling();
			resolvedVariant = 'control';
			pollingComplete = false;
		}
	};
};

export const useHolidayProductSelectorExperiment = (): {
	variant: HolidayProductSelectorExperimentSlot;
} => {
	const [holidayProductSelector, setHolidayProductSelector] =
		useState<HolidayProductSelectorExperimentSlot>('control');

	useEffect(() => subscribeToTargetDecision(setHolidayProductSelector), []);

	return {
		variant: holidayProductSelector
	};
};
